import { type Side } from "@design-lab/shared";
import {
  trackWeaveDesignGenerationFailed,
  trackWeaveDesignsGenerated,
  trackWeaveGenerateDesignPromptSubmitted
} from "@design-lab/shared/lib/analytics";
import { useGenerateAiImages } from "../api";
import { type GeneratedImage } from "../ui/components/AiImageGallery";
import { type AiDesignState } from "./state";

type UseAiImageGenerationOptions = {
  currentSide: Side;
  updateState: (updates: Partial<AiDesignState[Side]>) => void;
};

export function useAiImageGeneration({ currentSide, updateState }: UseAiImageGenerationOptions) {
  const generateImagesMutation = useGenerateAiImages();

  const handlePromptChange = (prompt: string) => {
    updateState({ prompt });
  };

  const handleGenerate = async (prompt: string) => {
    trackWeaveGenerateDesignPromptSubmitted(prompt);

    updateState({
      isGenerating: true,
      images: [], // Clear previous images
      error: undefined // Clear previous errors
    });

    try {
      const startTime = Date.now();
      const result = await generateImagesMutation.mutateAsync({ prompt });
      const duration = (Date.now() - startTime) / 1000; // Convert to seconds

      const apiImages: GeneratedImage[] = result.images.slice(0, 9).map((image, index) => ({
        id: image.aiImageVersionId,
        imageSrc: image.url,
        imageAlt: `Generated ${currentSide} artwork ${index + 1}`,
        style: image.style,
        provider: image.provider
      }));

      const designs = result.images.map(image => ({
        url: image.url,
        style: image.style || {}
      }));
      trackWeaveDesignsGenerated(duration, prompt, designs);

      updateState({
        isGenerating: false,
        images: apiImages
      });
    } catch (error: any) {
      console.error("Failed to generate images:", error);
      window.Honeybadger?.notify("Failed to generate images", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          hook: "useAiImageGeneration",
          prompt
        }
      });

      // Track generation failure
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred during generation";
      trackWeaveDesignGenerationFailed(prompt, errorMessage);
      const errorResponse =
        error?.response?.error ||
        "Looks like something in your prompt caused an issue. Adjust it and give it another shot.";

      updateState({
        isGenerating: false,
        error: errorResponse as string
      });
    }
  };

  return {
    handlePromptChange,
    handleGenerate
  };
}
